import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {ModalController} from 'ionic-angular';
import {cloneDeep, isEqual} from 'lodash';
import {Subscription} from 'rxjs/Subscription';
import {
  JackingFormConflictResult,
  JackingFormInstanceEditor
} from '../../models/jacking-v2/jacking.models';
import {PtLuxonDate} from '../../models/projects/date.models';
import {JackingMergeModalComponent} from '../../pages/jacking-activities/v2/components/jacking-merge-modal/jacking-merge-modal.component';
import {buildJackingDirtySnapshot} from './jacking-form-dirty-snapshot';
import {JackingV2Service} from './jacking-v2.service';
import {JackingMergeResult, JackingThreeWayMergeService} from './jacking-three-way-merge.service';
import {DataChangeNotification, SignalRService} from '../signalR/signalRService';

export type JackingConflictAction = 'save' | 'completion' | 'cancellation';

export interface JackingV2ConcurrentEditHost {
  editor: JackingFormInstanceEditor;
  dailyLogDate: PtLuxonDate;
  loading: boolean;
  isEditorDirtyForConcurrentChanges(): boolean;
  markEditorCleanForConcurrentChanges(editor: JackingFormInstanceEditor): void;
  commitConcurrentEditor(
    merged: JackingFormInstanceEditor,
    server: JackingFormInstanceEditor
  ): Promise<void>;
}

/** Page-scoped coordinator for the Jacking V2 concurrent-edit workflow. */
@Injectable()
export class JackingV2ConcurrentEditCoordinator {
  private host: JackingV2ConcurrentEditHost;
  private baseline: JackingFormInstanceEditor;
  private changeSubscription: Subscription;
  private mergeModalOpen = false;
  private mergeRefreshPending = false;
  private mergeServerRevision: string | null = null;
  private pendingMergeNotification: DataChangeNotification | null = null;
  private vesselId: number;

  externalChangePending = false;
  externalChangeNotification: DataChangeNotification | null = null;
  externalChangeHasLocalEdits = false;
  reviewingExternalChange = false;
  externalChangeReviewError = '';
  externalChangeResultMessage = '';
  externalChangeResultTitle = '';
  actionRequiredMessage = '';

  private revisionCheckTimer: number;
  private revisionCheckInFlight = false;
  private externalChangeResultTimer: number;
  private externalChangeEvaluationInFlight = false;
  private queuedExternalNotification: DataChangeNotification | null = null;

  constructor(
    private jackingV2Service: JackingV2Service,
    private modalController: ModalController,
    private signalRService: SignalRService,
    private mergeService: JackingThreeWayMergeService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  initialize(host: JackingV2ConcurrentEditHost, editor: JackingFormInstanceEditor, vesselId: number): void {
    this.host = host;
    this.baseline = cloneDeep(editor);
    this.vesselId = vesselId;
  }

  start(): void {
    this.subscribeToChanges();
    this.signalRService.connect(this.vesselId)
      .catch(error => console.error('Unable to connect to Jacking change notifications', error));
    this.startRevisionChecks();
  }

  stop(): void {
    this.unsubscribeFromChanges();
    this.stopRevisionChecks();
  }

  destroy(): void {
    this.stop();
    this.clearExternalChangeResultTimer();
  }

  resumeLiveTracking(): void {
    this.subscribeToChanges();
    this.startRevisionChecks();
  }

  async handleConflict(
    conflict?: JackingFormConflictResult,
    conflictAction: JackingConflictAction = 'save'
  ): Promise<void> {
    const server = await this.fetchLatestEditor();
    await this.reconcileWithServer(server);
    this.clearExternalChangeIfCurrent(server.revision);
    this.actionRequiredMessage = this.getRetryMessage(conflictAction, conflict?.message);
  }

  async reviewExternalChanges(): Promise<void> {
    if (!this.externalChangePending || this.reviewingExternalChange) {
      return;
    }

    const notificationAtStart = this.externalChangeNotification;
    const localWasDirty = this.host.isEditorDirtyForConcurrentChanges();
    this.reviewingExternalChange = true;
    this.externalChangeReviewError = '';
    try {
      const server = await this.fetchLatestEditor();
      if (server?.revision !== this.host.editor?.revision) {
        await this.reconcileWithServer(server);
      }

      if (this.externalChangeNotification === notificationAtStart ||
          this.externalChangeNotification?.revision === server?.revision) {
        this.clearExternalChangeIfCurrent(server?.revision);
        this.showExternalChangeResult(localWasDirty
          ? 'The latest server changes were merged with your entries. Review the form before saving.'
          : 'The latest version of the form has been loaded.',
          localWasDirty ? 'Changes reviewed' : 'Latest changes loaded');
      }
    } catch (error) {
      console.error('Unable to review external Jacking changes', error);
      this.externalChangeReviewError = 'The latest changes could not be loaded. Please try again.';
    } finally {
      this.reviewingExternalChange = false;
    }
  }

  private subscribeToChanges(): void {
    if (this.changeSubscription && !this.changeSubscription.closed) {
      return;
    }

    this.changeSubscription = this.signalRService.changes$.subscribe(notification => {
      if (!this.isRelevantNotification(notification)) {
        return;
      }
      if (notification.revision && notification.revision === this.host.editor?.revision) {
        return;
      }
      if (this.mergeModalOpen) {
        if (notification.revision !== this.mergeServerRevision) {
          this.mergeRefreshPending = true;
          this.pendingMergeNotification = notification;
        }
        return;
      }

      this.queueExternalChangeEvaluation(notification);
    });
  }

  private unsubscribeFromChanges(): void {
    if (this.changeSubscription) {
      this.changeSubscription.unsubscribe();
      this.changeSubscription = null;
    }
  }

  private isRelevantNotification(notification: DataChangeNotification): boolean {
    return !!notification &&
      Number(notification.vesselId) === Number(this.vesselId) &&
      notification.area === 'JackingV2' &&
      notification.entityType === 'JackingFormInstance' &&
      Number(notification.entityId) === Number(this.host.editor?.id) &&
      notification.clientInstanceId !== this.signalRService.clientInstanceId;
  }

  private fetchLatestEditor(): Promise<JackingFormInstanceEditor> {
    return this.jackingV2Service.getFormInstance(
      this.host.dailyLogDate.dateTime.toMillis(), Number(this.host.editor.id)
    );
  }

  private async reconcileWithServer(server: JackingFormInstanceEditor): Promise<void> {
    if (!server) {
      return;
    }

    const localIsClean = !this.host.isEditorDirtyForConcurrentChanges();
    if (localIsClean) {
      await this.commitMergedEditor(this.mergeService.synchronizeRepresentations(server), server);
      return;
    }

    const result = this.mergeService.merge(this.baseline || this.host.editor, this.host.editor, server);
    if (!result.conflicts.length && !result.incomingChanges.length) {
      await this.commitMergedEditor(result.mergedEditor, server);
      return;
    }

    await this.resolveConflicts(result, server);
  }

  private resolveConflicts(result: JackingMergeResult, server: JackingFormInstanceEditor): Promise<void> {
    this.mergeModalOpen = true;
    this.mergeRefreshPending = false;
    this.mergeServerRevision = server.revision;
    this.pendingMergeNotification = null;

    const modal = this.modalController.create(JackingMergeModalComponent, {
      conflicts: result.conflicts,
      incomingChanges: result.incomingChanges
    }, {
      cssClass: 'jacking-merge-modal',
      enableBackdropDismiss: false
    });

    return new Promise<void>((resolve, reject) => {
      modal.onDidDismiss(async (response?: {applied: boolean; choices: any}) => {
        this.mergeModalOpen = false;
        this.mergeServerRevision = null;
        this.resumeLiveTracking();
        try {
          if (this.mergeRefreshPending) {
            this.mergeRefreshPending = false;
            this.pendingMergeNotification = null;
            const latest = await this.fetchLatestEditor();
            await this.reconcileWithServer(latest);
          } else if (response?.applied) {
            const merged = this.mergeService.applyChoices(result, response.choices || {});
            await this.commitMergedEditor(merged, server);
          } else {
            // Conflicts must be resolved before the editor can safely adopt the newer revision.
            await this.resolveConflicts(result, server);
          }
          resolve();
        } catch (error) {
          reject(error);
        }
      });
      modal.present().then(() => this.subscribeToChanges());
    });
  }

  private async commitMergedEditor(
    merged: JackingFormInstanceEditor,
    server: JackingFormInstanceEditor
  ): Promise<void> {
    merged.revision = server.revision;
    this.baseline = cloneDeep(server);
    this.host.markEditorCleanForConcurrentChanges(this.mergeService.synchronizeRepresentations(server));
    await this.host.commitConcurrentEditor(merged, server);
  }

  private flagExternalChange(notification: DataChangeNotification): void {
    if (!notification || notification.revision === this.host.editor?.revision) {
      return;
    }
    this.externalChangeNotification = notification;
    this.blurActiveEditorControl();
    this.externalChangeHasLocalEdits = this.host.isEditorDirtyForConcurrentChanges();
    this.externalChangePending = true;
    this.externalChangeReviewError = '';
    this.externalChangeResultMessage = '';
    this.clearExternalChangeResultTimer();
  }

  private queueExternalChangeEvaluation(notification: DataChangeNotification): void {
    if (!notification || notification.revision === this.host.editor?.revision) {
      return;
    }

    // Every evaluation fetches the editor, so retaining only the latest queued signal is safe.
    // This also serializes bursts of SignalR and polling notifications.
    this.queuedExternalNotification = notification;
    if (this.externalChangeEvaluationInFlight) {
      return;
    }

    this.evaluateQueuedExternalChanges()
      .catch(error => console.error('Unable to evaluate external Jacking changes', error));
  }

  private async evaluateQueuedExternalChanges(): Promise<void> {
    this.externalChangeEvaluationInFlight = true;
    try {
      while (this.queuedExternalNotification) {
        const notification = this.queuedExternalNotification;
        this.queuedExternalNotification = null;

        if (notification.revision === this.host.editor?.revision) {
          continue;
        }

        try {
          const server = await this.fetchLatestEditor();
          if (!server || server.revision === this.host.editor?.revision) {
            continue;
          }

          if (this.isMeaningfulServerChange(server)) {
            this.flagExternalChange({...notification, revision: server.revision});
          } else {
            this.absorbSemanticallyEmptyRevision(server);
          }
        } catch (error) {
          // If the pre-flight fetch fails, retain the visible notification so Review changes
          // remains an explicit retry path for the user.
          this.flagExternalChange(notification);
          console.error('Unable to pre-load external Jacking changes', error);
        }
      }
    } finally {
      this.externalChangeEvaluationInFlight = false;
    }
  }

  private isMeaningfulServerChange(server: JackingFormInstanceEditor): boolean {
    return !isEqual(
      buildJackingDirtySnapshot(this.baseline || this.host.editor),
      buildJackingDirtySnapshot(server)
    );
  }

  private absorbSemanticallyEmptyRevision(server: JackingFormInstanceEditor): void {
    this.baseline = cloneDeep(server);
    this.host.markEditorCleanForConcurrentChanges(this.mergeService.synchronizeRepresentations(server));
    // Keep the editor object identity so an active input is not recreated or loses focus.
    // Only server-owned, dirty-excluded metadata needs to move forward for a semantic no-op.
    this.host.editor.revision = server.revision;
    this.host.editor.template = cloneDeep(server.template);
    this.host.editor.optionsByKey = cloneDeep(server.optionsByKey);
    this.host.editor.dataByKey = cloneDeep(server.dataByKey);
    this.clearExternalChangeIfCurrent(server.revision);
  }

  private clearExternalChangeIfCurrent(revision?: string): void {
    if (!this.externalChangeNotification || !revision ||
        this.externalChangeNotification.revision === revision) {
      this.externalChangePending = false;
      this.externalChangeNotification = null;
      this.externalChangeHasLocalEdits = false;
    }
  }

  private getRetryMessage(action: JackingConflictAction, serverMessage?: string): string {
    const prefix = serverMessage ? `${serverMessage} ` : '';
    if (action === 'completion') {
      return `${prefix}The form was not completed. Review the merged form, then choose Complete again.`;
    }
    if (action === 'cancellation') {
      return `${prefix}The form was not cancelled. Review the merged form, then choose Cancel activity again.`;
    }
    return `${prefix}Your previous save was not completed. Review the merged form, then press Save again.`;
  }

  private showExternalChangeResult(message: string, title: string): void {
    this.clearExternalChangeResultTimer();
    this.externalChangeResultTitle = title;
    this.externalChangeResultMessage = message;
    this.externalChangeResultTimer = window.setTimeout(() => {
      this.externalChangeResultMessage = '';
      this.externalChangeResultTitle = '';
      this.externalChangeResultTimer = null;
    }, 9000);
  }

  private blurActiveEditorControl(): void {
    const activeElement = this.document.activeElement as HTMLElement;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }

  private clearExternalChangeResultTimer(): void {
    if (this.externalChangeResultTimer) {
      window.clearTimeout(this.externalChangeResultTimer);
      this.externalChangeResultTimer = null;
    }
  }

  private startRevisionChecks(): void {
    if (this.revisionCheckTimer) {
      return;
    }
    this.revisionCheckTimer = window.setInterval(() => {
      this.checkForMissedRevision().catch(error => console.error('Unable to check Jacking revision', error));
    }, 5000);
  }

  private stopRevisionChecks(): void {
    if (this.revisionCheckTimer) {
      window.clearInterval(this.revisionCheckTimer);
      this.revisionCheckTimer = null;
    }
  }

  private async checkForMissedRevision(): Promise<void> {
    if (this.revisionCheckInFlight || this.host.loading || this.mergeModalOpen || this.reviewingExternalChange ||
        this.externalChangePending || !this.host.editor?.id || !this.host.dailyLogDate?.dateTime) {
      return;
    }

    this.revisionCheckInFlight = true;
    try {
      const summaries = await this.jackingV2Service.getSummariesForDate(this.host.dailyLogDate.dateTime.toMillis());
      const summary = (summaries || []).find(item => Number(item.id) === Number(this.host.editor.id));
      if (!summary?.revision || summary.revision === this.host.editor.revision) {
        return;
      }

      const status = String(summary.activityStatusName || summary.statusId || '').toLowerCase();
      const changeType = status === 'cancelled' ? 'Cancelled' :
        (status === 'completed' || status === 'confirmed' ? 'Completed' : 'Updated');
      this.queueExternalChangeEvaluation({
        vesselId: this.vesselId,
        area: 'JackingV2',
        entityType: 'JackingFormInstance',
        entityId: String(summary.id),
        changeType,
        revision: summary.revision,
        changedAtUtc: '',
        changedByDisplayName: summary.lastUpdatedBy,
        clientInstanceId: null
      });
    } finally {
      this.revisionCheckInFlight = false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/jacking-v2/jacking-v2-concurrent-edit-coordinator.service.ts